import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Box,
  IconButton,
  makeStyles,
  Tabs,
  Tab,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import DialogSearch from '../DialogSearch';
import NumberInputWithSpinner from './NumberInputWithSpinner';
import { customtheme } from '../../../app/theme/theme';
import { handleReturnValue } from '../../../common/functions/returnValue';
import { DialogSearchData } from '../../PurchaseOrderEmit';

const useStyles = makeStyles((theme) => ({
  textField: {
    marginTop: 8,
    marginBottom: 8,
  },
  backgroundColor: {
    background: customtheme.blue[500],
    color: customtheme.white,
    '&:hover': { background: customtheme.blue[50], color: customtheme.blue[500] },
  },
  searchIcon: {
    marginRight: theme.spacing(1),
    color: customtheme.blue[500],
  },
    tabsBar: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tabRoot: {
    minWidth: 0,
    paddingLeft: 8,
    paddingRight: 8,
  },
}));

interface TabPanelProps {
  value: number;
  index: number;
  children?: React.ReactNode;
}
const TabPanel = ({ value, index, children }: TabPanelProps) => (
  <div role="tabpanel" hidden={value !== index} style={{ height: '100%' }}>
    {value === index && <Box>{children}</Box>}
  </div>
);

interface Props {
  isOpen: boolean;
  fieldsInit: any[];
  onClose: () => void;
  checkPrevCompanyValue: { exists: boolean, value: any };
  checkPrevBranchValue: { exists: boolean, value: any };
  handleUpdateValue: (itens: any[]) => any;
}

const PurchaseRequestEmitItemDialog: React.FC<Props> = ({ isOpen, fieldsInit, onClose, handleUpdateValue, checkPrevCompanyValue, checkPrevBranchValue }) => {
  const classes = useStyles();

  const [isDialogSearchOpen, setIsDialogSearchOpen] = useState(false);
  const [dialogDataSearch, setDialogDataSearch] = useState<DialogSearchData | null>(null);

  const [fields, setFields] = useState<any[]>(fieldsInit);
  const [tab, setTab] = useState(fieldsInit.find((field) => field.id === 'tipo')?.value?.tipo === 'servico' ? 1 : 0);

  useEffect(() => {
    if (!isOpen) return;

    const today = new Date().toISOString().slice(0, 10);
    const nextFields = fieldsInit.map((field) => {
      if (field.id !== 'datprv') return field;

      const currentValue = field.value?.datprv || '';
      return {
        ...field,
        value: {
          datprv: currentValue || today,
        },
      };
    });

    setFields(nextFields);
    setTab(fieldsInit.find((field) => field.id === 'tipo')?.value?.tipo === 'servico' ? 1 : 0);
  }, [fieldsInit, isOpen]);

  const handleTabChange = (_: unknown, newValue: number) => {
    const update = [...fields];
    if (newValue === 1) {
      update[update.findIndex((field) => field.id === 'produto')].value = null

      update[update.findIndex((field) => field.id === 'tipo')].value = {
        tipo: 'servico'
      }
    } else {
      update[update.findIndex((field) => field.id === 'servico')].value = null
      update[update.findIndex((field) => field.id === 'tipo')].value = {
        tipo: 'produto'
      }
    }
    setFields(update)
    setTab(newValue);
  }

  const handleOpenDialogSearch = (data: DialogSearchData) => {
    setIsDialogSearchOpen(true);
    setDialogDataSearch(data);
  };

  const handleCloseDialogSearch = () => {
    setIsDialogSearchOpen(false);
    setDialogDataSearch(null);
  };

  const handleUpdateField = (data: any) => {
    const update = [...fields];

    // If we have an active dialog search, the incoming `data` is the selected item
    if (dialogDataSearch) {
      const targetId = dialogDataSearch.id === 'servicoOC' ? 'servico' : dialogDataSearch.id;
      const idx = update.findIndex((field) => field.id === targetId);
      if (idx !== -1) {
        // If the list returns a placeholder 'Nenhum item'
        if (data?.[dialogDataSearch.key || ''] === 'Nenhum item') {
          update[idx].value = null;
        } else {
          // Map returned data to the shape expected by mapperToPurchaseRequestItem
          switch (dialogDataSearch.id) {
            case 'produto':
              update[idx].value = {
                codpro: data.codpro,
                despro: data.despro,
              };
              break;
            case 'servico':
              update[idx].value = {
                codser: data.codser,
                desser: data.desser,
              };
              break;
            case 'deposito':
              update[idx].value = {
                coddep: data.coddep,
                desdep: data.desdep,
              };
              break;
            case 'centrosCusto':
              update[idx].value = {
                codccu: data.codccu,
                desccu: data.desccu,
              };
              break;
            case 'transacao':
              update[idx].value = {
                codtns: data.codtns,
                destns: data.destns,
              };
              break;
            default:
              update[idx].value = data;
          }
        }
      }

      // when selecting a product we may also receive `unimed` on the payload
      if (dialogDataSearch.id === 'produto' && data?.unimed) {
        const idxUni = update.findIndex((f) => f.id === 'unimed');
        if (idxUni !== -1) update[idxUni].value = data.unimed;
      }

      setFields(update);
      setIsDialogSearchOpen(false);
      setDialogDataSearch(null);
      return;
    }

    // Otherwise expect { id, value } from local inputs
    if (data?.id) {
      const idx = update.findIndex((f) => f.id === data.id);
      if (idx !== -1) {
        update[idx].value = data.value;
        setFields(update);
      }
    }
  }

  const setLowerKeys = (array: Array<any>) => {
    const arrChecked = !Array.isArray(array) ? [array] : array;
    return arrChecked.map(r =>
      Object.entries(r).reduce((acc: any, [key, value]: any) => {
        acc[key.toLowerCase()] = value;
        return acc;
      }, {}),
    );
  };

  const handleAddItem = () => {
    handleUpdateValue(fields);
  }

  const handleQuantitySpinnerChange = (value: number | null) => {
    const upd = [...fields];
    const idx = upd.findIndex(f => f.id === 'qtdsol');
    if (idx !== -1) {
      upd[idx].value = { qtdsol: value ? value : 0.00 };
      setFields(upd);
    }
  };

  // `valorunitario` is not part of the purchase request item mapper — removed.

  const handleSeqSolSpinnerChange = (value: number | null) => {
    const upd = [...fields];
    const idx = upd.findIndex(f => f.id === 'seqsol');
    if (idx !== -1) {
      upd[idx].value = { seqsol: value ? value : 0.00 };
      setFields(upd);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Adicionar/Editar Item</DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Tabs
                value={tab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                className={classes.tabsBar}
              >
                <Tab label="Produto" classes={{ root: classes.tabRoot }} />
                <Tab label="Serviço" classes={{ root: classes.tabRoot }} />
              </Tabs>
              <TabPanel value={tab} index={0}>
                {/* --- Produto --- */}
                <TextField
                  label="Produto"
                  onClick={() =>
                    handleOpenDialogSearch({
                      id: 'produto',
                      title: 'Produtos',
                      keySub: 'despro',
                      key: 'codpro',
                      prevDataSearch: {},
                      hasButtonCreate: false,
                    })
                  }
                  value={handleReturnValue('produto', fields)}
                  fullWidth
                  className={classes.textField}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        className={classes.searchIcon}
                        onClick={() =>
                          handleOpenDialogSearch({
                            id: 'produto',
                            title: 'Produtos',
                            keySub: 'despro',
                            key: 'codpro',
                            prevDataSearch: {},
                            hasButtonCreate: false,
                          })
                        }
                      >
                        <SearchIcon />
                      </IconButton>
                    ),
                  }}
                />

                {/* --- Derivação --- */}
                <TextField
                  label="Derivação"
                  disabled={!fields.find(f => f.id === 'produto')?.value}
                  onClick={
                    fields.find(f => f.id === 'produto')?.value
                      ? () =>
                        handleOpenDialogSearch({
                          id: 'derivacao',
                          title: 'Derivação',
                          keySub: 'desder',
                          key: 'codder',
                          isPrevSearch: true,
                          prevSearch: setLowerKeys(
                            fields.find(f => f.id === 'produto')?.value?.derivacao || [],
                          ),
                          prevDataSearch: {},
                          hasButtonCreate: false,
                        })
                      : undefined
                  }
                  value={
                    fields.find(f => f.id === 'produto')?.value
                      ? handleReturnValue('derivacao', fields)
                      : 'Selecione um produto primeiro'
                  }
                  fullWidth
                  className={classes.textField}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        className={classes.searchIcon}
                        disabled={!fields.find(f => f.id === 'produto')?.value}
                        onClick={
                          fields.find(f => f.id === 'produto')?.value
                            ? () =>
                              handleOpenDialogSearch({
                                id: 'derivacao',
                                title: 'Derivação',
                                keySub: 'desder',
                                key: 'codder',
                                isPrevSearch: true,
                                prevSearch: setLowerKeys(
                                  fields.find(f => f.id === 'produto')?.value?.derivacao || [],
                                ),
                                prevDataSearch: {},
                                hasButtonCreate: false,
                              })
                            : undefined
                        }
                      >
                        <SearchIcon />
                      </IconButton>
                    ),
                  }}
                />
              </TabPanel>

              <TabPanel value={tab} index={1}>
                <TextField
                  label="Serviço"
                  onClick={
                    checkPrevCompanyValue.exists ?
                      checkPrevBranchValue.exists ?
                        () => handleOpenDialogSearch({
                          id: 'servicoOC',
                          title: 'Serviço',
                          keySub: 'desser',
                          key: 'codser',
                          prevDataSearch: {
                            codemp: checkPrevCompanyValue.value,
                            codfil: checkPrevBranchValue.value,
                          },
                          hasButtonCreate: false,
                        })
                        : () => { }
                      : () => { }
                  }
                  value={
                    checkPrevCompanyValue.exists ?
                      checkPrevBranchValue.exists ?
                        handleReturnValue('servico', fields)
                        : 'Selecione uma filial primeiro'
                      : 'Selecione uma empresa primeiro'
                  }
                  disabled={
                    checkPrevCompanyValue.exists ?
                      checkPrevBranchValue.exists ?
                        false
                        : true
                      : true
                  }
                  fullWidth
                  className={classes.textField}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        className={classes.searchIcon}
                        onClick={
                          checkPrevCompanyValue.exists ?
                            checkPrevBranchValue.exists ?
                              () => handleOpenDialogSearch({
                                id: 'servicoOC',
                                title: 'Serviço',
                                keySub: 'desser',
                                key: 'codser',
                                prevDataSearch: {
                                  codemp: checkPrevCompanyValue.value,
                                  codfil: checkPrevBranchValue.value,
                                },
                                hasButtonCreate: false,
                              })
                              : () => { }
                            : () => { }
                        }
                        disabled={
                          checkPrevCompanyValue.exists ?
                            checkPrevBranchValue.exists ?
                              false
                              : true
                            : true
                        }
                      >
                        <SearchIcon />
                      </IconButton>
                    ),
                  }}
                />
              </TabPanel>
                </Grid>

                <Grid item xs={4}>
                   <NumberInputWithSpinner
                  label="Quantidade requerida"
                  value={fields.find(f => f.id === 'qtdsol')?.value?.qtdsol}
                  onChange={handleQuantitySpinnerChange}
                  step={0.01}
                  min={0.01}
                />

                </Grid>
            <Grid item xs={4}>
              <NumberInputWithSpinner label="Preço" value={fields.find(f => f.id === 'presol')?.value?.presol} onChange={(v) => handleUpdateField({ id: 'presol', value: { presol: v } })} step={0.01} min={0.01} />
            </Grid>
            <Grid item xs={4}>
              <NumberInputWithSpinner
                label="Sequencia"
                value={fields.find(f => f.id === 'seqsol')?.value?.seqsol}
                onChange={handleSeqSolSpinnerChange}
                step={1}
                min={1}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Transação"
                onClick={
                () => checkPrevCompanyValue.exists ? 
                    handleOpenDialogSearch({
                    id: 'transacao', 
                    title: 'Transação', 
                    keySub: 'destns', 
                    key: 'codtns',
                    prevDataSearch: { lismod: 'CSO' },
                    hasButtonCreate: false,
                    }) 
                    : () => {}
                }
                value={
                    checkPrevCompanyValue.exists ?
                        handleReturnValue('transacao', fields)
                    : 'Selecione uma empresa primeiro'
                }
                fullWidth
                className={classes.textField}
                disabled={
                  checkPrevCompanyValue.exists ? false : true
                }
                InputProps={{
                endAdornment: (
                    <IconButton
                    className={classes.searchIcon}
                    onClick={() => checkPrevCompanyValue.exists ? 
                        handleOpenDialogSearch({
                        id: 'transacao', 
                        title: 'Transação', 
                        keySub: 'destns', 
                        key: 'codtns',
                        prevDataSearch: { lismod: 'CSO' },
                        hasButtonCreate: false,
                        }) : 
                        () => {}
                    }
                    disabled={
                      checkPrevCompanyValue.exists ? false : true
                    }
                    >
                    <SearchIcon />
                    </IconButton>
                ),
                }}
            />
            </Grid>
            <Grid item xs={6}>
              <TextField
                  label="Data Previsão Entrega"
                  type="date"
                  value={fields.find(f => f.id === 'datprv')?.value?.datprv || ''}
                  onChange={e => {
                    const upd = [...fields];
                    upd[upd.findIndex(f => f.id === 'datprv')].value = { datprv: e.target.value };
                    setFields(upd);
                  }}
                  className={classes.textField}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Observação" value={fields.find(f => f.id === 'obssol')?.value?.obssol || ''} onChange={e => handleUpdateField({ id: 'obssol', value: { obssol: e.target.value } })} fullWidth className={classes.textField} />
            </Grid>

                <Grid item xs={6}>
                  <TextField
                    label="Unidade de medida"
                    value={fields.find(f => f.id === 'unimed')?.value || ''}
                    onChange={e => handleUpdateField({ id: 'unimed', value: e.target.value })}
                    fullWidth
                    className={classes.textField}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                   <TextField
                      label="Centro de Custo"
                      onClick={
                        checkPrevCompanyValue.exists ?
                          checkPrevBranchValue.exists ?
                            () => handleOpenDialogSearch({
                              id: 'centrosCusto',
                              title: 'Centro de Custo',
                              keySub: 'desccu',
                              key: 'codccu',
                              prevDataSearch: {
                                codemp: checkPrevCompanyValue.value,
                                codfil: checkPrevBranchValue.value,
                              },
                              hasButtonCreate: false,
                            })
                            : () => { }
                          : () => { }
                      }
                      value={
                        checkPrevCompanyValue.exists ?
                          checkPrevBranchValue.exists ?
                            handleReturnValue('centrosCusto', fields)
                          : 'Selecione uma filial primeiro'
                          : 'Selecione uma empresa primeiro'
                      }
                      fullWidth
                      className={classes.textField}
                      disabled={
                        checkPrevCompanyValue.exists ?
                          checkPrevBranchValue.exists ?
                            false
                          : true
                        : true
                      }
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            className={classes.searchIcon}
                            onClick={
                              checkPrevCompanyValue.exists ?
                                checkPrevBranchValue.exists ?
                                  () => handleOpenDialogSearch({
                                    id: 'centrosCusto',
                                    title: 'Centro de Custo',
                                    keySub: 'desccu',
                                    key: 'codccu',
                                    prevDataSearch: {
                                      codemp: checkPrevCompanyValue.value,
                                      codfil: checkPrevBranchValue.value,
                                    },
                                    hasButtonCreate: false,
                                  })
                                : () => { }
                              : () => { }
                            }
                            disabled={
                              checkPrevCompanyValue.exists ?
                                checkPrevBranchValue.exists ?
                                  false
                                : true
                              : true
                            }
                          >
                            <SearchIcon />
                          </IconButton>
                        ),
                      }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                <TextField
                        label="Depósito"
                        onClick={() => handleOpenDialogSearch({
                        id: 'deposito', 
                        title: 'Depósito', 
                        keySub: 'desdep', 
                        key: 'coddep',
                        prevDataSearch: {},
                        hasButtonCreate: false,
                        })}
                        value={handleReturnValue('deposito', fields)}
                        fullWidth
                        className={classes.textField}
                        InputProps={{
                        endAdornment: (
                            <IconButton 
                            className={classes.searchIcon} 
                            onClick={() => handleOpenDialogSearch({
                                id: 'deposito', 
                                title: 'Depósito',
                                keySub: 'desdep',
                                key: 'coddep',
                                prevDataSearch: {},
                                hasButtonCreate: false,
                            })}>
                            <SearchIcon />
                            </IconButton>
                        ),
                        }}
                    />
                </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Voltar</Button>
        <Button onClick={handleAddItem} className={classes.backgroundColor}>Confirmar</Button>
      </DialogActions>

      <DialogSearch
        codEmp={checkPrevCompanyValue.value}
        codFil={checkPrevBranchValue.value}
        title={dialogDataSearch?.title || ''}
        id={dialogDataSearch?.id || ''}
        keyItem={dialogDataSearch?.key || ''}
        keySubItem={dialogDataSearch?.keySub || ''}
        isDialogOpen={isDialogSearchOpen}
        isPrevSearch={dialogDataSearch?.isPrevSearch || false}
        prevSearch={dialogDataSearch?.prevSearch || []}
        prevDataSearch={dialogDataSearch?.prevDataSearch}
        handleCloseDialog={handleCloseDialogSearch}
        handleUpdateField={(data: any) => { handleUpdateField(data) }}
        hasButtonCreate={false}
      />
    </Dialog>
  );
};

export default PurchaseRequestEmitItemDialog;
